import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  FunctionField,
  Filter,
  SelectInput,
  TopToolbar,
  CreateButton,
  useTranslate,
} from 'react-admin';
import { Box, Chip, Typography } from '@mui/material';

const PlaybookFilter = (props: any) => {
  const translate = useTranslate();
  return (
    <Filter {...props}>
      <SelectInput
        source="isActive"
        label={translate('resources.ai-playbooks.filters.isActive')}
        choices={[
          { id: 'true', name: translate('resources.ai-playbooks.filters.active') },
          { id: 'false', name: translate('resources.ai-playbooks.filters.inactive') },
        ]}
        alwaysOn
      />
    </Filter>
  );
};

const ListActions = () => (
  <TopToolbar>
    <CreateButton />
  </TopToolbar>
);

// Category chip from rootCauseCategory; a muted "—" placeholder when null (hand-authored playbooks
// may have a null category per REF-01 s.1.2).
const CategoryCell = ({ category }: { category: string | null }) => {
  const translate = useTranslate();
  if (!category) {
    return (
      <Typography component="span" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Chip
      size="small"
      label={translate(`resources.ai-playbooks.categories.${category}`, { _: category })}
    />
  );
};

// Learned (from an investigation) vs manually authored, derived from createdFromInvestigationId.
// Does NOT render the raw id (the Show view links it).
const SourceCell = ({ createdFromInvestigationId }: { createdFromInvestigationId: string | null }) => {
  const translate = useTranslate();
  return createdFromInvestigationId ? (
    <Chip size="small" color="info" label={translate('resources.ai-playbooks.fields.learned')} />
  ) : (
    <Chip size="small" variant="outlined" label={translate('resources.ai-playbooks.fields.manual')} />
  );
};

// Standard List + Datagrid: name, category chip, isActive, learned-vs-manual indicator, updatedAt.
// Lists all playbooks (active + inactive) by default; the isActive filter narrows.
export const PlaybookList = () => {
  const translate = useTranslate();
  return (
    <List
      filters={<PlaybookFilter />}
      actions={<ListActions />}
      sort={{ field: 'created_at', order: 'DESC' }}
      perPage={25}
      title={translate('resources.ai-playbooks.list.title')}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="name" label={translate('resources.ai-playbooks.fields.name')} />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.rootCauseCategory')}
          render={(record: any) => <CategoryCell category={record.rootCauseCategory ?? null} />}
        />
        <BooleanField source="isActive" label={translate('resources.ai-playbooks.fields.isActive')} />
        <FunctionField
          label={translate('resources.ai-playbooks.fields.source')}
          render={(record: any) => (
            <SourceCell createdFromInvestigationId={record.createdFromInvestigationId ?? null} />
          )}
        />
        <DateField
          source="updatedAt"
          showTime
          label={translate('resources.ai-playbooks.fields.updatedAt')}
        />
        <Box />
      </Datagrid>
    </List>
  );
};

export default PlaybookList;
